import React, { memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StopSummary } from 'components/marketplace/StopSummary'
import { Button } from 'transfix-ui/components/Button'
import ClipboardCopy from 'transfix-ui/components/Copy'
import { IShipment } from 'types'
import { openDialog as openCoverDialog } from 'utils/actions/ui'
import {
  isTenderedShipment,
  selectShipmentAssignedDriver,
  selectShipmentCoveredBid,
} from 'apps/booking/utils/selectors/shipment'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { LaneArrowIcon } from 'transfix-icons'
import { getNextStatus, stopsSummary, shipmentDetailsString } from 'utils/shipmentHelpers'
import { DIALOG_UPDATE_STATUSES } from 'constants/shipments'

interface IHeaderProps {
  shipment: IShipment
}

interface IActionButtonProps {
  shipment?: IShipment
  actionMethod: (actionData: any) => void
  actionText: string
}

export const Header = ({ shipment }: IHeaderProps) => {
  const { delivery, pickup } = stopsSummary(shipment)
  return (
    <div className='pb1 flex items-center' data-test='shipment-header'>
      {pickup && <StopSummary stop={pickup} variation='large' />}
      <div className='mh4'>
        <LaneArrowIcon size='large' />
      </div>
      {delivery && <StopSummary stop={delivery} variation='large' />}
    </div>
  )
}

export const ActionButtons = memo(({ actionMethod, actionText, shipment }: IActionButtonProps) => {
  const { trackEvent } = useMPAnalytics()
  const isTenderedStatus = useSelector(isTenderedShipment)
  const isDriverAssignSubmitSuccess = useSelector(selectShipmentAssignedDriver)
  const hasCoveredBid = useSelector(selectShipmentCoveredBid)
  const dispatch = useDispatch()
  if (!shipment) return null
  const textToCopy = shipmentDetailsString(shipment)
  const openDialog = () => {
    let nextStatus = getNextStatus(DIALOG_UPDATE_STATUSES, shipment.status.state)
    if (nextStatus === 'In Transit') nextStatus = 'Picked Up'
    if (nextStatus) return actionMethod({ id: 'update_status', statusUpdate: nextStatus })
    return actionMethod({ id: 'option' })
  }

  const isPendingOrTendered = isTenderedStatus || hasCoveredBid
  const showCoverLoadBtn = isPendingOrTendered && !isDriverAssignSubmitSuccess

  const coverCtaClickHandler = (e: any) => {
    dispatch(openCoverDialog({ id: 'coverFlow' }))

    if (e && e.currentTarget) {
      trackEvent('Booking', 'CLICKED_COVER_CTA', {
        cta: e.currentTarget.innerText,
        shipmentId: shipment.id,
      })
    }
  }

  return (
    <>
      <ClipboardCopy
        copyText={textToCopy}
        displayText={`Shipment #${shipment.id} details`}
        alignClass='right'
        useIcon
      />
      <Button
        className='btn-primary'
        data-test='add-option-or-confirm-shipment-button'
        onClick={openDialog}>
        {actionText}
      </Button>
      {showCoverLoadBtn && (
        <Button data-test='cover-load-button' onClick={coverCtaClickHandler}>
          {isTenderedStatus ? 'Cover' : 'Assign Driver'}
        </Button>
      )}
    </>
  )
})
